/* eslint-disable @typescript-eslint/no-explicit-any */
import { ErrorHandler, NgModule } from '@angular/core'
import { BrowserModule } from '@angular/platform-browser'
import { RouteReuseStrategy } from '@angular/router'
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'

import {
  provideIonicAngular,
  IonicRouteStrategy,
  IonApp,
  IonRouterOutlet,
  IonIcon,
  IonFabButton,
  IonInput,
  IonSplitPane,
  IonContent,
  IonBadge,
  IonBackButton,
  IonGrid,
  IonRow,
  IonCol,
  IonMenu,
  IonItem,
  IonLabel,
  IonMenuToggle,
  IonToolbar,
  IonTitle,
  IonHeader,
  IonSearchbar,
  IonChip,
  IonButton,
  IonButtons,
  IonAvatar,
  IonListHeader,
  IonList,
  IonCard,
  IonCardHeader,
  IonCardContent,
  IonSelect,
  IonSelectOption,
  IonCardSubtitle,
  IonCardTitle,
  IonMenuButton,
  IonCheckbox,
  IonAccordion,
  IonAccordionGroup,
} from '@ionic/angular/standalone'
import { ModalController, PopoverController } from '@ionic/angular/standalone'
import { SplashScreen } from '@awesome-cordova-plugins/splash-screen/ngx'
import { StatusBar } from '@awesome-cordova-plugins/status-bar/ngx'

import { AppComponent } from './app.component'
import { AppRoutingModule } from './app-routing.module'
import { CoreModule } from './core/core.module'
import { CanDeactivateGuard } from './core/can-deactivate.guard'

import { PlainTextPageModule } from './modals/plain-text/plain-text.module'

import { CreateEditSubcollectionPageModule } from 'src/app/site/app/collections/subcollection/create-edit-subcollection/create-edit-subcollection.module'
import { CreateEditUsersPageModule } from 'src/app/site/app/users/create-edit-users/create-edit-users.module'
import { Vibration } from '@awesome-cordova-plugins/vibration/ngx'
import { ComponentsModule } from './components/components.module'

import { AppPageModule } from './site/app/app.module'
import { FormsModule, FormBuilder, ReactiveFormsModule } from '@angular/forms'
import { NfcSelectProductPageModule } from './/modals/nfc-select-product/nfc-select-product.module'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { ServiceWorkerModule } from '@angular/service-worker'
import { environment } from '../environments/environment'

import { ToastModule } from 'primeng/toast'
import { RippleModule } from 'primeng/ripple'
import { MessageService } from 'primeng/api'
import { ConfirmationService } from 'primeng/api'
import { ConfirmDialogModule } from 'primeng/confirmdialog'
import { enterAnimation } from './animations/nav-animation'
import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx'
import { ErrorHandlerService } from './services/error-handler.service'
import { httpInterceptorProviders } from './http-interceptors'
import { SelectModule } from 'primeng/select'
import { CreateBuildingPageModule } from './site/app/collections/create-building/create-building.module'
import { AutoFocusModule } from 'primeng/autofocus'
import { Typeguards } from './typeGuards/typeguards'
import { SlateModule } from 'slate-angular'
import { TextMarkComponent } from './components/text-mark-component/text-mark.component'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { FloorplateHasFloorplanPipe } from './pipes/floorplate-has-floorplan.pipe'
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import {
  faStar as farStar,
  faCloudArrowDown as farCloudArrowDown,
  faUser as farUser,
  faUserGroup as farUserGroup,
  faFiles as farFiles,
  faFile as farFile,
  faWebhook as farWebHook,
  faPenToSquare as farPenToSquare,
  faCirclePlay as farCirclePlay,
  faPlus as farPlus,
  faArrowUpRightFromSquare as farArrowUpRightFromSquare,
  faShareNodes as farShareNodes,
  faFileCheck as farFileCheck,
  faFileWord as farFileWord,
  faRectangleHistory as farRectangleHistory,
  faCheck as farCheck,
  faDownload as farDownload,
  faMobile as farMobile,
  faAt as farAt,
  faQrcode as farQrcode,
  faXmark as farXmark,
  faNfc as farNfc,
  faCircleExclamation as farCircleExclamation,
  faMagnifyingGlass as farMagnifyingGlass,
  faCircleInfo as farCircleInfo,
  faArrowRightFromBracket as farArrowRightFromBracket,
  faCircleCheck as farCircleCheck,
  faLink as farLink,
  faLinkSlash as farLinkSlash,
  faArrowsMaximize as farArrowsMaximize,
  faCirclePlus as farCirclePlus,
  faCircleMinus as farCircleMinus,
  faCircleXmark as farCircleXmark,
  faShieldCheck as farShieldCheck,
  faShieldXmark as farShieldXmark,
  faHistory as farHistory,
  faMinus as farMinus,
  faMagnifyingGlassMinus as farMagnifyingGlassMinus,
  faMagnifyingGlassPlus as farMagnifyingGlassPlus,
  faList as farList,
  faListTree as farListTree,
  faTrash as farTrash,
  faEye as farEye,
  faNotes as farNotes,
  faSquare as farSquare,
  faSquareCheck as farSquareCheck,
  faBuilding as farBuilding,
  faEllipsis as farEllipsis,
  faExpand as farExpand,
  faX as farX,
  faText as farText,
  faLocationPin as farLocationPin,
  faLocationDot as farLocationDot,
  faCloud as farCloud,
  faGears as farGears,
  faBallot as farBallot,
  faCopy as farCopy,
  faArrowUpFromLine as farArrowUpFromLine,
  faArrowDownFromLine as farArrowDownFromLine,
  faCamera as farCamera,
  faCameraViewfinder as farCameraViewfinder,
  faRightToBracket as farRightToBracket,
  faLeftToBracket as farLeftToBracket,
  faTag as farTag,
  faGripLines as farGripLines,
  faUpDown as farUpDown,
  faLock as farLock,
  faBookmark as farBookmark,
  faChartBar as farChartBar,
  faArrowsRotate as farArrowsRotate,
  faKey as farKey,
  faTriangleExclamation as farTriangleExclamation,
  faObjectsAlignLeft as farObjectsAlignLeft,
  faObjectsAlignTop as farObjectsAlignTop,
  faObjectsAlignRight as farObjectsAlignRight,
  faAlignLeft as farAlignLeft,
  faAlignCenter as farAlignCenter,
  faAlignRight as farAlignRight,
  faArrowUpFromBracket as farArrowUpFromBracket,
  faArrowDownToBracket as farArrowDownToBracket,
  faPaintbrushFine as farPaintbrushFine,
  faImage as farImage,
  faImages as farImages,
  faBold as farBold,
  faItalic as farItalic,
  faListUl as farListUl,
  faCalendar as farCalendar,
  faListOl as farListOl,
  faChevronRight as farChevronRight,
  faChevronDown as farChevronDown,
  faDoorClosed as farDoorClosed,
  faCube as farCube,
  faTable as farTable,
  faCode as farCode,
  faWifiSlash as farWifiSlash,
  faGripDotsVertical as farGripDotsVertical,
  faWandMagicSparkles as farWandMagicSparkles,
  faRotate as farRotate,
  faFireExtinguisher as farFireExtinguisher,
  faArrowLeft as farArrowLeft,
  faArrowRight as farArrowRight,
  faFloppyDisk as farFloppyDisk,
  faFileLines as farFileLines,
  faFire as farFire,
  faHouse as farHouse,
  faGlobe as farGlobe,
  faToolbox as farToolbox,
  faScrewdriverWrench as farScrewdriverWrench,
  faListCheck as farListCheck,
  faBolt as farBolt,
  faDroplet as farDroplet,
  faWind as farWind,
  faPipeSection as farPipeSection,
  faChevronLeft as farChevronLeft,
  faDoorOpen as farDoorOpen,
  faLayerGroup as farLayerGroup,
  faGear as farGear,
  faClipboardCheck as farClipboardCheck,
  faSliders as farSliders,
  faClipboard as farClipboard,
  faShapes as farShapes,
  faHashtag as farHashtag,
  faStamp as farStamp,
  faClock as farClock,
  faCircleDot as farCircleDot,
  faPaperclip as farPaperclip,
  faSignature as farSignature,
  faHeading as farHeading,
  faSquareChevronDown as farSquareChevronDown,
  faToggleOn as farToggleOn,
  faEllipsisVertical as farEllipsisVertical,
  faGrid2 as farGrid2,
  faUsers as farUsers,
  faFileImport as farFileImport,
  faBuildings as farBuildings,
  faChartLine as farChartLine,
  faFlask as farFlask,
  faCheckDouble as farCheckDouble,
  faCircleQuestion as farCircleQuestion,
  faCloudSlash as farCloudSlash,
  faSidebar as farSidebar,
  faSidebarFlip as farSidebarFlip,
  faCubes as farCubes,
  faFileSignature as farFileSignature,
  faCalendarCheck as farCalendarCheck,
  faSparkles as farSparkles,
  faInbox as farInbox,
  faEyeSlash as farEyeSlash,
  faMegaphone as farMegaphone,
  faArrowUp as farArrowUp,
  faArrowDown as farArrowDown,
  faArrowTrendUp as farArrowTrendUp,
  faArrowTrendDown as farArrowTrendDown,
  faClone as farClone,
  faCloudArrowUp as farCloudArrowUp,
} from '@fortawesome/pro-regular-svg-icons'
import {
  farTableColumnsAddAfter,
  farTableColumnsRemoveAfter,
  farTableRowsAddBelow,
  farTableRowsRemoveBelow,
} from './components/wysiwyg/fa-table-icons'
import {
  faStar as fasStar,
  faCloudArrowUp as fasCloudArrowUp,
  faEnvelope as fasEnvelope,
  faPhone as fasPhone,
  faUser as fasUser,
  faList as fasList,
  faShareNodes as fasShareNodes,
  faGrid2 as fasGrid2,
  faEye as fasEye,
  faCloud as fasCloud,
  faSignal as fasSignal,
  faX as fasX,
  faMicrophone as fasMicrophone,
  faVideo as fasVideo,
  faCircleNotch as fasCircleNotch,
  faSpinnerScale as fasSpinnerScale,
} from '@fortawesome/pro-solid-svg-icons'
import {
  faCirclePlus as falCirclePlus,
  faCircleExclamation as falCircleExclamation,
  faLocationDot as falLocationDot,
  faShieldCheck as falShieldCheck,
  faShieldXmark as falShieldXmark,
  faFileImport as falFileImport,
  faFile as falFile,
  faCamera as falCamera,
  faBallot as falBallot,
  faRectangleHistory as falRectangleHistory,
  faFiles as falFiles,
  faTag as falTag,
  faList as falList,
} from '@fortawesome/pro-light-svg-icons'
import { faStars as fassStars } from '@fortawesome/sharp-solid-svg-icons'
import { DialogService } from 'primeng/dynamicdialog'
import { ProgressBarModule } from 'primeng/progressbar'
import { Capacitor } from '@capacitor/core'

import { providePrimeNG } from 'primeng/config'
import PrimeNgAuraPreset from './core/primeNG_preset'
import { RootGalleriaComponent } from './components/root-galleria/root-galleria.component'

@NgModule({
  declarations: [AppComponent, TextMarkComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    IonApp,
    IonRouterOutlet,
    ReactiveFormsModule,
    FormsModule,
    IonIcon,
    IonFabButton,
    IonCard,
    IonCardSubtitle,
    IonCardTitle,
    IonCardContent,
    IonCardHeader,
    IonInput,
    IonContent,
    IonBadge,
    IonBackButton,
    IonGrid,
    IonRow,
    IonCol,
    IonSelect,
    IonSelectOption,
    IonSplitPane,
    IonMenu,
    IonItem,
    IonLabel,
    IonMenuToggle,
    IonToolbar,
    IonTitle,
    IonHeader,
    IonSearchbar,
    IonChip,
    IonButton,
    IonButtons,
    IonAvatar,
    IonListHeader,
    IonList,
    IonMenuButton,
    IonCheckbox,
    IonAccordion,
    IonAccordionGroup,
    AppRoutingModule,
    PlainTextPageModule,
    CreateEditSubcollectionPageModule,
    CreateBuildingPageModule,
    CreateEditUsersPageModule,
    // InspectionViewPageModule,
    NfcSelectProductPageModule,
    CoreModule,
    ComponentsModule,
    // FileUploadModule,
    AppPageModule,
    ToastModule,
    ConfirmDialogModule,
    ProgressBarModule,
    RippleModule,
    MatDatepickerModule,
    SlateModule,
    FontAwesomeModule,
    SelectModule,
    BrowserAnimationsModule,
    AutoFocusModule,
    RootGalleriaComponent,
    ServiceWorkerModule.register('ngsw-worker.js', {
      // enabled: true,
      enabled: !Capacitor.isNativePlatform() && environment.serviceWorkersEnabled,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:10000',
    }),
  ],
  providers: [
    StatusBar,
    FormBuilder,
    SplashScreen,
    Vibration,
    FileOpener,
    ModalController,
    PopoverController,
    IonIcon,
    IonFabButton,
    Typeguards,
    httpInterceptorProviders,
    CanDeactivateGuard,
    MessageService,
    ConfirmationService,
    DialogService,
    FloorplateHasFloorplanPipe,
    providePrimeNG({
      theme: {
        options: {
          darkModeSelector: false,
          ripple: true,
        },
        preset: PrimeNgAuraPreset,
      },
    }),
    provideIonicAngular({
      mode: 'ios',
      navAnimation: enterAnimation,
    }),
    // MultiFileUploadComponent,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    provideHttpClient(withInterceptorsFromDi()),
    // provideAnimationsAsync('noop'),
  ],
})
export class AppModule {
  constructor(library: FaIconLibrary) {
    // Add multiple icons to the library
    library.addIcons(
      farStar,
      farGripDotsVertical as any,
      farWandMagicSparkles as any,
      farRotate as any,
      farFireExtinguisher as any,
      farArrowLeft as any,
      farArrowRight as any,
      farFloppyDisk as any,
      farFileLines as any,
      farFire as any,
      farHouse as any,
      farGlobe as any,
      farToolbox as any,
      farScrewdriverWrench as any,
      farListCheck as any,
      farBolt as any,
      farDroplet as any,
      farWind as any,
      farPipeSection as any,
      farChevronLeft as any,
      farDoorOpen as any,
      farLayerGroup as any,
      farGear as any,
      farClipboardCheck as any,
      farSliders as any,
      farClipboard as any,
      farShapes as any,
      farHashtag as any,
      farStamp as any,
      farClock as any,
      farCircleDot as any,
      farPaperclip as any,
      farSignature as any,
      farHeading as any,
      farSquareChevronDown as any,
      farToggleOn as any,
      farEllipsisVertical as any,
      fasStar as any,
      farCloudArrowDown as any,
      farCloudArrowUp as any,
      fasCloudArrowUp as any,
      farUserGroup as any,
      farWebHook as any,
      fasEnvelope as any,
      fasPhone as any,
      fasUser as any,
      farShareNodes as any,
      farRectangleHistory as any,
      farFileCheck as any,
      farFileWord as any,
      farArrowUpRightFromSquare as any,
      fasList as any,
      fasShareNodes as any,
      farFiles as any,
      farFile as any,
      farCirclePlay as any,
      farPenToSquare as any,
      fasGrid2 as any,
      farCheck as any,
      farDownload as any,
      farPlus as any,
      farUser as any,
      farMobile as any,
      farAt as any,
      farQrcode as any,
      farXmark as any,
      farNfc as any,
      farCircleExclamation as any,
      farMagnifyingGlass as any,
      farCircleInfo as any,
      farArrowRightFromBracket as any,
      farCircleCheck as any,
      farLink as any,
      farArrowsMaximize as any,
      farCirclePlus as any,
      falCirclePlus as any,
      farCircleMinus as any,
      farCircleXmark as any,
      falCircleExclamation as any,
      farShieldCheck as any,
      farShieldXmark as any,
      farCircleXmark as any,
      farLinkSlash as any,
      falLocationDot as any,
      farHistory as any,
      farMinus as any,
      farMagnifyingGlassMinus as any,
      farMagnifyingGlassPlus as any,
      farList as any,
      farListTree as any,
      farTrash as any,
      farEye as any,
      fasEye as any,
      farNotes as any,
      falFile as any,
      farSquare as any,
      farSquareCheck as any,
      farBuilding as any,
      farEllipsis as any,
      farExpand as any,
      farX as any,
      farText as any,
      farLocationPin as any,
      farLocationDot as any,
      farCloud as any,
      fasSignal as any,
      fasCloud as any,
      fasX as any,
      farGears as any,
      farBallot as any,
      falShieldCheck as any,
      falShieldXmark as any,
      fassStars as any,
      fasMicrophone as any,
      fasVideo as any,
      fasCircleNotch as any,
      fasSpinnerScale as any,
      falFileImport as any,
      farCopy as any,
      farArrowUpFromLine as any,
      farCamera as any,
      farCameraViewfinder as any,
      falCamera as any,
      farArrowDownFromLine as any,
      farRightToBracket as any,
      farLeftToBracket as any,
      farTag as any,
      farGripLines as any,
      farUpDown as any,
      farLock as any,
      farBookmark as any,
      falBallot as any,
      falRectangleHistory as any,
      falFiles as any,
      falTag as any,
      falList as any,
      farChartBar as any,
      farArrowsRotate as any,
      farKey as any,
      farTriangleExclamation as any,
      farObjectsAlignLeft as any,
      farObjectsAlignTop as any,
      farObjectsAlignRight as any,
      farAlignLeft as any,
      farAlignCenter as any,
      farAlignRight as any,
      farArrowUpFromBracket as any,
      farArrowDownToBracket as any,
      farPaintbrushFine as any,
      farImage as any,
      farImages as any,
      farBold as any,
      farItalic as any,
      farListUl as any,
      farCalendar as any,
      farListOl as any,
      farChevronRight as any,
      farChevronDown as any,
      farDoorClosed as any,
      farCube as any,
      farTable as any,
      farCode as any,
      farWifiSlash as any,
      farTableColumnsAddAfter as any,
      farTableColumnsRemoveAfter as any,
      farTableRowsAddBelow as any,
      farTableRowsRemoveBelow as any,
      farGrid2 as any,
      farUsers as any,
      farFileImport as any,
      farBuildings as any,
      farChartLine as any,
      farFlask as any,
      farCheckDouble as any,
      farCircleQuestion as any,
      farCloudSlash as any,
      farSidebar as any,
      farSidebarFlip as any,
      farCubes as any,
      farFileSignature as any,
      farCalendarCheck as any,
      farSparkles as any,
      farInbox as any,
      farEyeSlash as any,
      farMegaphone as any,
      farArrowUp as any,
      farArrowDown as any,
      farArrowTrendUp as any,
      farArrowTrendDown as any,
      farClone as any,
    )
  }
}
